/**
 * ProfilePicturePicker — bird grid + photo upload modal.
 * Web port of client-native ProfilePicturePickerModal.tsx.
 *
 * 4 bird avatars (72px), selected = blue 3px border + CheckCircle2 badge.
 * "— or —" divider, then Take Photo (solid blue, camera capture) and Upload
 * Photo (white, blue border, file input). On web, custom images become data URLs.
 */
import { FC, useRef } from "react";
import { Camera, Images, CheckCircle2 } from "lucide-react";
import { colors } from "@/ui";
import ProfileModal from "./ProfileModal";
import {
  BIRD_AVATARS,
  BirdAvatarKey,
  getAllBirdAvatarKeys,
} from "./avatarUtils";

interface ProfilePicturePickerProps {
  open: boolean;
  selectedBird: BirdAvatarKey | null;
  onClose: () => void;
  onSelectBird: (key: BirdAvatarKey) => void;
  onSelectCustomImage: (dataUrl: string) => void;
}

const BIRD_SIZE = 72;

const ProfilePicturePicker: FC<ProfilePicturePickerProps> = ({
  open,
  selectedBird,
  onClose,
  onSelectBird,
  onSelectCustomImage,
}) => {
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const birdKeys = getAllBirdAvatarKeys();

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== "string") return;
      const original = reader.result;
      // Downscale before handing back a data URL. A raw photo's base64 can be
      // multiple MB and overflow the ~5MB localStorage quota (where the avatar
      // is persisted), throwing on save. Cap to 512px and re-encode as JPEG so
      // the stored string stays small (~tens of KB). Fall back to the original
      // if the image can't be decoded/drawn.
      const img = new Image();
      img.onload = () => {
        try {
          const MAX = 512;
          const scale = Math.min(1, MAX / Math.max(img.width, img.height));
          const w = Math.max(1, Math.round(img.width * scale));
          const h = Math.max(1, Math.round(img.height * scale));
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            onSelectCustomImage(original);
          } else {
            ctx.drawImage(img, 0, 0, w, h);
            onSelectCustomImage(canvas.toDataURL("image/jpeg", 0.85));
          }
        } catch {
          onSelectCustomImage(original);
        }
        onClose();
      };
      img.onerror = () => {
        onSelectCustomImage(original);
        onClose();
      };
      img.src = original;
    };
    reader.readAsDataURL(file);
  };

  return (
    <ProfileModal open={open} onClose={onClose}>
      <span className="text-lg font-bold" style={{ color: colors.primary }}>
        Choose Profile Picture
      </span>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 16,
          marginTop: 20,
        }}
      >
        {birdKeys.map((birdKey) => {
          const isSelected = selectedBird === birdKey;
          return (
            <button
              key={birdKey}
              type="button"
              onClick={() => {
                onSelectBird(birdKey);
                onClose();
              }}
              style={{
                position: "relative",
                width: BIRD_SIZE,
                height: BIRD_SIZE,
                borderRadius: BIRD_SIZE / 2,
                padding: 0,
                overflow: "hidden",
                cursor: "pointer",
                border: `${isSelected ? 3 : 1}px solid ${
                  isSelected ? colors.blue : colors.greyLight
                }`,
                backgroundColor: colors.white,
              }}
            >
              <img
                src={BIRD_AVATARS[birdKey]}
                alt={birdKey}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
              {isSelected && (
                <div
                  style={{
                    position: "absolute",
                    bottom: -2,
                    right: -2,
                    borderRadius: 12,
                    backgroundColor: colors.white,
                    lineHeight: 0,
                  }}
                >
                  <CheckCircle2 size={24} color={colors.blue} />
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          gap: 8,
          margin: "20px 0",
        }}
      >
        <div style={{ flex: 1, height: 1, backgroundColor: colors.greyLight }} />
        <span className="text-sm" style={{ color: colors.muted }}>
          or
        </span>
        <div style={{ flex: 1, height: 1, backgroundColor: colors.greyLight }} />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%" }}>
        <button
          type="button"
          onClick={() => cameraInputRef.current?.click()}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            backgroundColor: colors.blue,
            color: colors.white,
            padding: "12px 24px",
            borderRadius: 24,
            border: "none",
            cursor: "pointer",
          }}
        >
          <Camera size={20} color={colors.white} />
          <span className="text-base font-semibold">Take Photo</span>
        </button>
        <button
          type="button"
          onClick={() => uploadInputRef.current?.click()}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            backgroundColor: colors.white,
            color: colors.blue,
            padding: "12px 24px",
            borderRadius: 24,
            border: `2px solid ${colors.blue}`,
            cursor: "pointer",
          }}
        >
          <Images size={20} color={colors.blue} />
          <span className="text-base font-semibold">Upload Photo</span>
        </button>
      </div>

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="user"
        style={{ display: "none" }}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      <input
        ref={uploadInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </ProfileModal>
  );
};

export default ProfilePicturePicker;
